import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {Strand} from 'constants/MolbioConstants';
import {BiointelligentLinkType} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {
  LINKED_ENTITY_CHIP_HORIZONTAL_PADDING,
  LINKED_ENTITY_CHIP_ICON_SIZE,
  LINKED_ENTITY_CHIP_VERTICAL_PADDING,
  LINKED_ENTITY_CHIP_HEIGHT,
  SINGLE_ANNOTATION_RADIUS,
} from 'molbio/maps/BiointelligentLinksConstants';
import {getLinkChipTextColor, getLinkArrowColor} from 'molbio/maps/linear/BiointelligentLinksHelpers';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface BiointelligentLinkChipProps {
  linkName: string;
  linkIcon: BenchlingIconName;
  linkType: BiointelligentLinkType;
  annotationWidth: number;
  chipMaxWidth: number;
  linkDirection?: Strand;
}

/**
 * Chip-like component for use inside of the complex nested SVGs in the sequence map.
 *
 * It's a bit less awkward to use a foreignObject for the entire chip and style it with
 * CSS here - in part because we would need a foreignObject anyways for the icon, in
 * part because it's a pain to center things programmatically with svg
 */
export default function BiointelligentLinkChip({
  linkName,
  linkIcon,
  linkType,
  annotationWidth,
  chipMaxWidth,
  linkDirection,
}: BiointelligentLinkChipProps): JSX.Element {
  const isReversed = linkDirection === -1;
  return (
    <foreignObject
      height={LINKED_ENTITY_CHIP_HEIGHT}
      width={annotationWidth}
      x={isReversed ? -LINKED_ENTITY_CHIP_HORIZONTAL_PADDING : LINKED_ENTITY_CHIP_HORIZONTAL_PADDING}
      y={LINKED_ENTITY_CHIP_VERTICAL_PADDING * 2}
    >
      <div
        className={css(styles.chipContainer)}
        style={{justifyContent: isReversed ? 'flex-end' : 'flex-start'}}
      >
        <span
          className={css(styles.chip)}
          style={{
            // Make sure there is always at least enough space for the icon and padding (12px + 3 * 2px)
            maxWidth: Math.max(18, chipMaxWidth),
            color: getLinkChipTextColor(linkType),
            backgroundColor: getLinkArrowColor(linkType),
          }}
        >
          <BenchlingIcon icon={linkIcon} className={css(styles.icon)} />
          <span
            className={css(styles.chipText)}
            style={{maxWidth: chipMaxWidth - LINKED_ENTITY_CHIP_HORIZONTAL_PADDING * 2}}
          >
            {linkName}
          </span>
        </span>
      </div>
    </foreignObject>
  );
}

const styles = StyleSheet.create({
  chipContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  chip: {
    borderRadius: SINGLE_ANNOTATION_RADIUS,
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,
    padding: `${LINKED_ENTITY_CHIP_VERTICAL_PADDING}px ${LINKED_ENTITY_CHIP_HORIZONTAL_PADDING}px`,
    cursor: 'pointer',
  },
  chipText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  icon: {
    height: LINKED_ENTITY_CHIP_ICON_SIZE,
    width: LINKED_ENTITY_CHIP_ICON_SIZE,
    marginRight: LINKED_ENTITY_CHIP_HORIZONTAL_PADDING,
  },
});
